/**
 * ThreadStrip -- stitching review move 5.2.
 *
 * One service's thread in five stages, always in this order and with
 * the same words (docs/the-story.md section 4): what the regulator
 * expects, what the bank has, what the product judged, what a named
 * person decided, and what can be proved. Each stage says who holds it
 * and offers the next step, which is the page's answer to "where now?".
 *
 * Reads GET /v1/thread/ibs/{id}. Derived on the server on every read,
 * so the strip can never disagree with the pages it links to.
 */
import { listOf } from "shared/i18n/listOf";
import { i18n } from "shared/i18n";
import { formatDate } from "shared/time/formatDate";
import type { TFunction } from "i18next";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { MeOut, ThreadStage, ThreadStageStatus } from "shared/api/types";
import {
  canReadBoard,
  canReadCeremony,
  canRunOperations,
  hasModule,
} from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import { FirstMeetings } from "shared/glossary/FirstMeetings";
import { roleLabel, stateLabel } from "shared/vocab/workflowState";
import { type ThreadSubject, useThread } from "shared/thread/useThread";

const STATUS_CLASS: Record<ThreadStageStatus, string> = {
  done: "border-emerald-200 bg-emerald-50 text-emerald-800",
  attention: "border-amber-200 bg-amber-50 text-amber-900",
  missing: "border-slate-200 bg-slate-50 text-slate-600",
};

function num(v: number | string | null | undefined): number {
  return typeof v === "number" ? v : 0;
}

function word(v: number | string | null | undefined): string {
  return typeof v === "string" && v ? v : "";
}

/** The finding thread's sentences: the same stages, read from an
 *  incident -- the reporting duty, where it lands, the plans answering it. */
function findingSummary(stage: ThreadStage, t: TFunction): string {
  const f = stage.facts;
  const base = "modules.twin.thread.finding";
  switch (stage.key) {
    case "expected":
      // Round 19 G35 -- a change's finding asks for no regulator report.
      if (word(f.change)) return t(`${base}.expectedChange`);
      if (stage.status === "missing") return t(`${base}.expectedNone`);
      return t(`${base}.expected`, {
        count: num(f.reports),
        submitted: num(f.submitted),
        late: num(f.late),
      });
    case "have": {
      if (stage.status === "missing") return t(`${base}.haveNone`);
      // Round 20 P20 -- supported directly, and reached through.
      const through =
        f.services_direct == null
          ? ""
          : num(f.dependent_services) > 0
            ? t(`${base}.haveThrough`, {
                direct: num(f.services_direct),
                count: num(f.dependent_services),
              })
            : t(`${base}.haveThroughNone`, { direct: num(f.services_direct) });
      // Round 18 P8 -- name the cut when candidates or a funding path are
      // in the reach; a plain admitted reach reads as before.
      if (
        num(f.services_reached_all) > num(f.services_reached) ||
        num(f.via_funding) > 0
      ) {
        return (
          t(`${base}.haveCut`, {
            name: word(f.entity_name),
            count: num(f.services_reached),
            all: Math.max(num(f.services_reached_all), num(f.services_reached)),
            via: num(f.via_funding) > 0 ? t(`${base}.haveVia`) : "",
            mvb: num(f.mvb_reached),
            // Round 19 P15 -- the minimum-viable figure names its cut too.
            mvbAll: Math.max(num(f.mvb_reached_all), num(f.mvb_reached)),
          }) + through
        );
      }
      return (
        t(`${base}.have`, {
          name: word(f.entity_name),
          count: num(f.services_reached),
          mvb: num(f.mvb_reached),
        }) + through
      );
    }
    case "judged": {
      const line = t(`${base}.judged`, {
        severity: stateLabel(word(f.severity)),
        status: stateLabel(word(f.finding_status)),
        breached: num(f.breached),
        at_risk: num(f.at_risk),
      });
      // Round 17 R4 -- a function's own breach, beside the register's.
      // Round 19 R21 -- and the services under assessment it breaches.
      return [
        line,
        num(f.functions_breached) > 0
          ? t(`${base}.judgedFunctions`, { count: num(f.functions_breached) })
          : null,
        num(f.candidates_breached) > 0
          ? t(`${base}.judgedCandidates`, { count: num(f.candidates_breached) })
          : null,
      ]
        .filter(Boolean)
        .join(" ");
    }
    case "decided":
      if (num(f.caps) === 0) {
        return stage.status === "done"
          ? t(`${base}.decidedClosed`)
          : t(`${base}.decidedNone`);
      }
      return t(`${base}.decided`, { count: num(f.caps), approved: num(f.approved) });
    case "proved":
      return t(`${base}.proved`, {
        count: num(f.caps_sealed),
        reports: num(f.reports_submitted),
      });
  }
}

/** The letter thread's sentences: the asks, what answers them, and
 *  whose seat each plan waits in. */
function letterSummary(stage: ThreadStage, t: TFunction): string {
  const f = stage.facts;
  const base = "modules.twin.thread.letter";
  switch (stage.key) {
    case "expected":
      if (stage.status === "missing") return t(`${base}.expectedNone`);
      return t(`${base}.expected`, {
        count: num(f.observations),
        serious: num(f.serious),
      });
    case "have":
      if (stage.status === "missing") return t(`${base}.haveNone`);
      return t(`${base}.have`, {
        controls: num(f.controls),
        findings: num(f.findings),
      });
    case "judged":
      if (num(f.observations) === 0) return t(`${base}.judgedNone`);
      return t(`${base}.judged`, {
        answered: num(f.answered),
        count: num(f.observations),
      });
    case "decided":
      if (num(f.caps) === 0) return t(`${base}.decidedNone`);
      return t(`${base}.decided`, { count: num(f.caps), approved: num(f.approved) });
    case "proved":
      return t(`${base}.proved`, {
        count: num(f.caps_sealed),
        complete: num(f.actions_complete),
        actions: num(f.actions),
      });
  }
}

/** The scenario thread's sentences: whether the regime asks for it,
 *  what it strikes, the verdict, the recorded basis, the runs. */
function scenarioSummary(stage: ThreadStage, t: TFunction): string {
  const f = stage.facts;
  const base = "modules.twin.thread.scenario";
  switch (stage.key) {
    case "expected":
      return num(f.required) > 0
        ? t(`${base}.expectedRequired`, { regime: word(f.regime).toUpperCase() })
        : num(f.citations) > 0
          ? t(`${base}.expectedCited`, { count: num(f.citations) })
          : t(`${base}.expectedNone`);
    case "have":
      if (stage.status === "missing") return t(`${base}.haveNone`);
      return t(`${base}.have`, { count: num(f.targets) });
    case "judged": {
      if (stage.status === "missing") return t(`${base}.judgedNone`);
      // Round 18 R10 -- the functions judged beside the services, and
      // the breached counted across both; plural-aware ("1 services").
      const fns = num(f.functions_assessed);
      // Round 20 P24 -- one population per figure. The sum of services
      // and functions breached read "5 breached" beside the war room's
      // "4 of 5"; the services are counted, the functions judged apart.
      const candidates = num(f.candidates_assessed);
      const servicesLine = t(`${base}.judgedServicesOf`, {
        count: num(f.assessed),
        breached: num(f.breached),
        at_risk: num(f.at_risk),
        cand:
          candidates > 0
            ? t(`${base}.judgedServicesCandidates`, { n: candidates })
            : "",
      });
      if (fns === 0) return servicesLine;
      const fnCand = num(f.functions_candidates);
      const status =
        f.functions_candidates == null
          ? ""
          : fnCand === fns
            ? t(`${base}.judgedFnUnderAssessment`)
            : fnCand === 0
              ? t(`${base}.judgedFnAdmitted`)
              : t(`${base}.judgedFnSome`, { n: fnCand });
      return (
        servicesLine +
        t(`${base}.judgedFunctionsApart`, {
          count: fns,
          status,
          breached: num(f.functions_breached),
        })
      );
    }
    case "decided":
      if (stage.status === "missing") return t(`${base}.decidedNone`);
      if (num(f.review_overdue) > 0) return t(`${base}.decidedOverdue`);
      return word(f.approved_by)
        ? t(`${base}.decidedApproved`, { name: word(f.approved_by) })
        : t(`${base}.decidedUnapproved`);
    case "proved":
      if (num(f.runs) > 0) {
        return t(`${base}.proved`, {
          count: num(f.runs),
          date: formatDate(word(f.last_run_at), i18n.language),
        });
      }
      return num(f.planned) > 0
        ? t(`${base}.provedPlanned`, { count: num(f.planned) })
        : t(`${base}.provedNone`);
  }
}

/** One sentence per stage, from the figures the server names. */
export function stageSummary(
  stage: ThreadStage,
  t: TFunction,
  subjectKind = "ibs_service",
  isFunction = false,
): string {
  if (subjectKind === "finding") return findingSummary(stage, t);
  if (subjectKind === "letter") return letterSummary(stage, t);
  if (subjectKind === "scenario") return scenarioSummary(stage, t);
  const f = stage.facts;
  const base = "modules.twin.thread.summary";
  switch (stage.key) {
    case "expected":
      if (stage.status === "missing") return t(`${base}.expectedNone`);
      return t(`${base}.expected`, {
        controls: num(f.controls),
        assessed: num(f.assessed),
        gaps: num(f.gaps),
      });
    case "have":
      if (stage.status === "missing") return t(`${base}.haveNone`);
      return t(`${base}.have`, {
        count: num(f.supporting_services),
        unmapped: num(f.unmapped_layers),
        register: stateLabel(word(f.register)),
      });
    case "judged":
      // Round 19 B23 -- an `unknown` seal is a seal: "No resilience state
      // has been sealed yet" stood over one written at 20:44:26Z.
      if (stage.status === "missing" && word(f.resilience) === "unknown") {
        return t(`${base}.judgedUnknown`, {
          date: formatDate(word(f.sealed_at), i18n.language),
        });
      }
      if (stage.status === "missing") return t(`${base}.judgedNone`);
      return t(`${base}.judged`, {
        resilience: t(`modules.twin.thread.rag.${word(f.resilience) || "unknown"}`, {
          defaultValue: stateLabel(word(f.resilience)),
        }),
        count: num(f.open_findings),
      });
    case "decided": {
      const decidedSentence = (): string => {
        if (stage.status === "missing") return t(`${base}.decidedNone`);
        const tolerance = t(
          `modules.twin.thread.tolerance.${word(f.tolerance) || "undeclared"}`,
        );
        // Round 17 R1 -- an approved deferral is a deferral. Round 18 R9
        // -- the API says "deferred" / "excluded" itself now; an older
        // payload says "approved" beside the inclusion.
        const inclusion = word(f.inclusion);
        const admission = word(f.admission);
        if (
          admission === "deferred" ||
          admission === "excluded" ||
          (admission === "approved" && inclusion && inclusion !== "included")
        ) {
          return inclusion === "excluded" || admission === "excluded"
            ? t(`${base}.decidedExcluded`, { tolerance })
            : t(`${base}.decidedDeferred`, { tolerance });
        }
        // Round 18 R9 -- mid-Route 2 the CRO has signed and the second
        // seat is named, not "the second signature".
        if (admission === "cro_signed" && word(f.awaits)) {
          return t(`${base}.decidedAwaiting`, {
            role: roleLabel(word(f.awaits)),
            tolerance,
          });
        }
        return t(`${base}.decided`, {
          admission: stateLabel(word(f.admission)),
          tolerance,
        });
      };
      // Round 19 R20 -- a tolerance leg in flight is named beside the
      // admission's sentence: "Tolerance awaiting Sara Al-Hassan (CRO)".
      const tolAwaits = word(f.tolerance_awaits);
      const tolLine = tolAwaits
        ? " " +
          t(`${base}.decidedToleranceAwaiting`, {
            who:
              stage.holder.role === tolAwaits && stage.holder.names.length > 0
                ? t("modules.twin.thread.signerInSeat", {
                    name: listOf([...new Set(stage.holder.names)]),
                    role: roleLabel(tolAwaits),
                  })
                : roleLabel(tolAwaits),
          })
        : "";
      // Round 20 V1 -- challenged and then signed by one person: the
      // challenge was not independent, whatever the signatures say.
      const notIndependent = word(f.challenger_signed)
        ? " " +
          t(`${base}.decidedNotIndependent`, {
            name: word(f.challenger) || "—",
            role: roleLabel(word(f.challenger_signed)),
          })
        : "";
      // Round 20 A46 -- signature times earlier than the record.
      const backdated = word(f.signed_before_created)
        ? " " +
          t(`${base}.decidedBackdated`, {
            legs: word(f.signed_before_created)
              .split(",")
              .map((leg) =>
                leg === "challenge"
                  ? t(`${base}.decidedBackdatedChallenge`)
                  : roleLabel(leg),
              )
              .join(", "),
            created: formatDate(word(f.record_created_at), i18n.language),
          })
        : "";
      return decidedSentence() + tolLine + notIndependent + backdated;
    }
    case "proved":
      if (stage.status === "missing")
        return t(isFunction ? `${base}.provedNoneFunction` : `${base}.provedNone`);
      // Round 18 R9 -- what the seal is of.
      return t(
        word(f.sealed) === "deferral"
          ? `${base}.provedDeferral`
          : word(f.sealed) === "exclusion"
            ? `${base}.provedExclusion`
            : `${base}.proved`,
        { date: formatDate(word(f.admission_sealed_at), i18n.language) },
      );
  }
}

export function holderLine(
  stage: ThreadStage,
  t: TFunction,
  language?: string,
): string {
  const role = roleLabel(stage.holder.role);
  // Round 19 R22 -- the day as every other surface says it ("10 Oct
  // 2026"), not ISO.
  const on = formatDate(stage.holder.on ?? null, language ?? i18n.language);
  // Round 17 R2 -- each person once, whatever the payload says.
  const names = [...new Set(stage.holder.names)];
  // Round 18 R9 -- a decision two seats signed names each in their
  // seat: "Admitted by Sara Al-Hassan (CRO) and Rana Al-Qahtani (CIO)".
  const roles = stage.holder.roles ?? [];
  if (
    stage.holder.verb === "approved" &&
    roles.length > 0 &&
    roles.length === stage.holder.names.length
  ) {
    const signers = stage.holder.names.map((n, i) =>
      t("modules.twin.thread.signerInSeat", { name: n, role: roleLabel(roles[i]!) }),
    );
    const act = word(stage.facts.admission);
    return t(
      act === "deferred"
        ? "modules.twin.thread.deferredBy"
        : act === "excluded"
          ? "modules.twin.thread.excludedBy"
          : "modules.twin.thread.admittedBy",
      { signers: listOf(signers), date: on },
    );
  }
  // Round 19 R19 -- a sealed record is held by those who signed it:
  // "Sealed on 10 Oct 2026 after Sara Al-Hassan (CRO) and Rana
  // Al-Qahtani (CIO) signed", not the Approver seat and its count.
  if (stage.holder.verb === "sealed" && names.length > 0) {
    const signers =
      roles.length === stage.holder.names.length
        ? stage.holder.names.map((n, i) =>
            t("modules.twin.thread.signerInSeat", {
              name: n,
              role: roleLabel(roles[i]!),
            }),
          )
        : names;
    return t("modules.twin.thread.sealedAfter", { signers: listOf(signers), date: on });
  }
  // Round 18 R7 -- a decision taken names who took it and when.
  if (stage.holder.verb === "approved" && names.length > 0) {
    return t("modules.twin.thread.approvedBy", {
      names: listOf(names),
      date: on,
    });
  }
  if (names.length === 0) {
    const count = stage.holder.count ?? 0;
    return count > 1
      ? t("modules.twin.thread.heldBySeatMany", { role, count })
      : t("modules.twin.thread.heldBySeat", { role });
  }
  return t("modules.twin.thread.heldBy", { names: listOf(names), role });
}

/**
 * Round 17 R3 -- may this seat take the step? The owner's "Assess the
 * controls" and the treasurer's "Open the board" landed on refusals.
 * Mirrors the guard of the page each step opens; a seat that cannot
 * open it reads who takes the step instead.
 */
const FRAMEWORK_STEPS = new Set([
  "assess_controls",
  "open_cap",
  "link_cap",
  "record_observations",
  "link_controls",
  "answer_observations",
  "raise_cap",
  "complete_actions",
]);
const OPERATIONS_STEPS = new Set([
  "record_basis",
  "record_test_run",
  "cite_regime",
  "add_targets",
  "record_report",
]);

export function seatMayTake(action: string, me: MeOut | undefined): boolean {
  if (!me) return true;
  if (FRAMEWORK_STEPS.has(action)) return hasModule(me, "framework");
  if (action === "open_board") return canReadBoard(me);
  if (action === "open_admission") return canReadCeremony(me);
  if (OPERATIONS_STEPS.has(action)) return canRunOperations(me);
  return true;
}

export function ThreadStrip({
  subject = "ibs",
  id,
  ibsId,
  capId,
}: {
  subject?: ThreadSubject;
  id?: string | undefined;
  /** The service page's original prop, kept so its call reads as before. */
  ibsId?: string | undefined;
  /** Round 18 R7 -- a letter's thread read from one of its plans. */
  capId?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const query = useThread(subject, id ?? ibsId, capId);
  const me = useMe();
  // Round 17 R6 -- the strip did not draw on a few scenario views: it
  // returned nothing while its record loaded. It says it is reading.
  if (query.isLoading) {
    return (
      <p data-testid="thread-strip-loading" className="mt-4 text-xs text-slate-500">
        {t("modules.twin.thread.loading")}
      </p>
    );
  }
  // The strip is an addition to a page that stands without it: a seat
  // without the module, or a moment of failure, shows the page as before.
  if (!query.data || !Array.isArray(query.data.stages)) return null;
  const thread = query.data;
  return (
    <section
      data-testid="thread-strip"
      aria-label={t("modules.twin.thread.heading")}
      className="mt-4"
    >
      <h2 className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {t("modules.twin.thread.heading")}
      </h2>
      {/* Round 17 R6 -- what a thread is, taught once. */}
      <FirstMeetings terms={["thread"]} className="mt-2" />
      <ol className="mt-2 grid gap-2 sm:grid-cols-5">
        {thread.stages.map((stage) => (
          <li
            key={stage.key}
            data-testid="thread-stage"
            data-stage={stage.key}
            data-status={stage.status}
            className={`rounded-lg border p-3 text-sm ${STATUS_CLASS[stage.status]}`}
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-medium">
                {t(`modules.twin.thread.stage.${stage.key}`)}
              </span>
              <span className="text-xs">
                {t(`modules.twin.thread.status.${stage.status}`)}
              </span>
            </div>
            <p className="mt-0.5 text-xs opacity-80">
              {t(`modules.twin.thread.question.${stage.key}`)}
            </p>
            <p className="mt-2" data-testid="thread-stage-summary">
              {stageSummary(stage, t, thread.subject_kind, thread.is_function)}
            </p>
            {/* Round 20 Y8 -- "sealed" with something to inspect: the
                seal's hash, and how to check it. */}
            {stage.key === "proved" &&
              typeof stage.facts?.admission_seal_hash === "string" && (
                <p data-testid="thread-seal" className="mt-1 text-xs">
                  {t("modules.twin.thread.sealHash", {
                    hash: String(stage.facts.admission_seal_hash).slice(0, 16),
                  })}{" "}
                  <Link
                    to="/glossary#seal"
                    data-testid="thread-seal-verify"
                    className="text-brand-brassDark hover:underline"
                  >
                    {t("modules.twin.thread.sealHowToVerify")}
                  </Link>
                </p>
              )}
            {(stage.links ?? []).length > 0 && (
              <ul
                data-testid="thread-stage-links"
                className="mt-1 flex flex-wrap gap-x-2 text-xs"
              >
                {(stage.links ?? []).map((l) => (
                  <li key={l.href}>
                    <Link
                      to={l.href}
                      className="font-mono text-brand-brassDark hover:underline"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1 text-xs" data-testid="thread-stage-holder">
              {holderLine(stage, t, i18n.language)}
            </p>
            {stage.next && !seatMayTake(stage.next.action, me.data) && (
              <p data-testid="thread-stage-not-yours" className="mt-2 text-xs">
                {/* Round 19 R22 -- name the person to ask where the record
                    knows one, not only the seat. */}
                {stage.holder.names.length > 0
                  ? t("modules.twin.thread.notYoursNamed", {
                      names: listOf([...new Set(stage.holder.names)]),
                      role: roleLabel(stage.holder.role),
                    })
                  : t("modules.twin.thread.notYours", {
                      role: roleLabel(stage.holder.role),
                    })}
              </p>
            )}
            {stage.next && seatMayTake(stage.next.action, me.data) && (
              <Link
                to={stage.next.href}
                data-testid="thread-stage-next"
                className="mt-2 inline-block text-xs font-medium text-brand-brassDark hover:underline"
              >
                {t(`modules.twin.thread.next.${stage.next.action}`)}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
